import { ArrowDown } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { isRtl, languageName, type Language } from "@/lib/languages.ts";
import type { Layout } from "@/lib/prefs.ts";
import type { Direction, Segment, TextPair } from "@/lib/protocol.ts";

type Props = {
  segments: Segment[];
  languages: Language[];
  direction: Direction;
  speakerA: string;
  layout: Layout;
  showSource: boolean;
  textScale: number;
  live: boolean;
  empty: ReactNode;
};

function Text({ pair, caret }: { pair: TextPair; caret?: boolean }) {
  return (
    <>
      {pair.stable}
      {pair.stable && pair.pending ? " " : null}
      {pair.pending ? <span className="pending">{pair.pending}</span> : null}
      {caret ? <span className="live-caret" aria-hidden="true" /> : null}
    </>
  );
}

function Listening() {
  return (
    <span className="listening-dots text-muted" aria-label="Speech detected">
      <span>●</span> <span>●</span> <span>●</span>
    </span>
  );
}

function clock(s: number) {
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
}

function Meta({ seg, languages, twoWay, isB }: { seg: Segment; languages: Language[]; twoWay: boolean; isB: boolean }) {
  return (
    <div className="mb-1 flex items-center gap-2 text-[11px] tracking-wide text-faint uppercase tabular-nums">
      <span>{clock(seg.startS)}</span>
      <span
        className={`rounded px-1.5 py-0.5 font-medium normal-case ${
          twoWay ? (isB ? "bg-speaker-b-soft text-speaker-b" : "bg-accent-soft text-accent") : "bg-surface-2 text-muted"
        }`}
      >
        {languageName(languages, seg.lang)} → {languageName(languages, seg.targetLang)}
      </span>
      {seg.incomplete ? <span className="text-danger normal-case">incomplete</span> : null}
      {seg.final && !seg.targetFinal ? <span className="normal-case">refining…</span> : null}
    </div>
  );
}

export function TranscriptView(props: Props) {
  const { segments, languages, direction, speakerA, layout, showSource, textScale, live, empty } = props;
  const scroller = useRef<HTMLDivElement>(null);
  const [pinned, setPinned] = useState(true);
  const twoWay = direction === "two-way";

  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && pinned) el.scrollTop = el.scrollHeight;
  });

  useEffect(() => {
    if (!live) setPinned(true);
  }, [live]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    setPinned(el.scrollHeight - el.scrollTop - el.clientHeight < 64);
  };

  const finals = segments.filter((s) => s.final && s.targetFinal);
  const lastFinal = finals[finals.length - 1];

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scroller}
        onScroll={onScroll}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6"
        style={{ fontSize: `${textScale}rem` }}
      >
        {segments.length === 0 ? (
          <div className="flex h-full items-center justify-center text-center text-muted">{empty}</div>
        ) : layout === "columns" ? (
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            {segments.map((seg, i) => {
              const isB = twoWay && seg.lang !== speakerA;
              const last = i === segments.length - 1 && live;
              const noText = !seg.source.stable && !seg.source.pending;
              return (
                <div key={seg.id} className="contents">
                  <div lang={seg.lang} dir={isRtl(seg.lang) ? "rtl" : "ltr"}>
                    <Meta seg={seg} languages={languages} twoWay={twoWay} isB={isB} />
                    <p className="text-[1.05em] leading-relaxed text-muted">
                      {noText ? <Listening /> : <Text pair={seg.source} caret={last && !seg.final} />}
                    </p>
                  </div>
                  <div lang={seg.targetLang} dir={isRtl(seg.targetLang) ? "rtl" : "ltr"} className="pt-5">
                    <p className="font-display text-[1.45em] leading-snug">
                      <Text pair={seg.target} />
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <ol className="flex flex-col gap-6">
            {segments.map((seg, i) => {
              const isB = twoWay && seg.lang !== speakerA;
              const last = i === segments.length - 1 && live;
              const noText = !seg.source.stable && !seg.source.pending;
              return (
                <li
                  key={seg.id}
                  className={`border-s-2 ps-4 ${twoWay ? (isB ? "border-speaker-b" : "border-accent") : "border-line"} ${
                    seg.final ? "" : "opacity-95"
                  }`}
                >
                  <Meta seg={seg} languages={languages} twoWay={twoWay} isB={isB} />
                  {showSource ? (
                    <p
                      lang={seg.lang}
                      dir={isRtl(seg.lang) ? "rtl" : "ltr"}
                      className="text-[1em] leading-relaxed text-muted"
                    >
                      {noText ? <Listening /> : <Text pair={seg.source} caret={last && !seg.final && !seg.target.stable && !seg.target.pending} />}
                    </p>
                  ) : null}
                  {seg.target.stable || seg.target.pending || !showSource ? (
                    <p
                      lang={seg.targetLang}
                      dir={isRtl(seg.targetLang) ? "rtl" : "ltr"}
                      className="mt-1 font-display text-[1.45em] leading-snug text-fg"
                    >
                      {!showSource && noText ? <Listening /> : <Text pair={seg.target} caret={last && !seg.final} />}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ol>
        )}
      </div>
      {!pinned ? (
        <button
          type="button"
          className="btn absolute right-4 bottom-4 border-accent bg-surface shadow-lg"
          onClick={() => {
            setPinned(true);
            scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
          }}
        >
          <ArrowDown size={16} /> Latest
        </button>
      ) : null}
      {/* Screen readers hear each translation once, when it is final. */}
      <p className="sr-only" aria-live="polite">
        {lastFinal ? [lastFinal.target.stable, lastFinal.target.pending].filter(Boolean).join(" ") : ""}
      </p>
    </div>
  );
}
